// Curated, grass-matched seed + starter picks for the seeding calculator.
// "The seed for YOUR grass" instead of a generic category block: maps the
// result's grass type to that species' seed in the shared catalog
// (data/guide-product-recommendations.ts) plus a starter-fertilizer companion.
// Mirrors lib/soil-test/product-picks.ts via the shared resolver.
//
// The grass -> seed map itself lives in lib/product-picks/seed-by-grass.ts so the
// grass-comparison tool can reuse the same enthusiast-grade picks. St. Augustine
// is intentionally unmapped there (sod/plugs, not seed), so this returns [] for it
// and the page falls back to the generic block.

import type { ProductRecommendationLink } from '@/lib/product-picks/types'
import { resolvePicks } from '@/lib/product-picks/resolve'
import {
  SEED_BY_GRASS,
  SHADE_SEED_PICKS,
  STARTER,
  normalizeGrass,
  shadeSeedKind,
} from '@/lib/product-picks/seed-by-grass'
import { getGrassCharacteristics } from '@/data/grass-characteristics'
import { normalizeSunlight } from '@/lib/lawn-plan-engine/normalization'
import { buildToolResultPick, upperBoundLbs, type ToolResultPickData } from '@/lib/tool-result-pick'

/**
 * Curated seed + starter picks matched to the seeding result's grass type.
 * Returns [] for grasses with no seed product (e.g. St. Augustine) so the caller
 * can fall back to the generic block. Pure.
 */
export function getSeedingPicks(result: { grassType: string; sunlight?: string | null }): ProductRecommendationLink[] {
  const seed = SEED_BY_GRASS[normalizeGrass(result.grassType)]
  if (!seed) return []
  const shade = shadeSeedPickFor(result.grassType, result.sunlight)
  return resolvePicks(shade ? [shade, seed, STARTER] : [seed, STARTER])
}

/** The shade blend a grass in this sun should be seeded with, or null when its own seed fits. */
export function shadeSeedPickFor(grassType: string, sunlight?: string | null) {
  const grass = getGrassCharacteristics(grassType)
  const kind = shadeSeedKind({
    season: grass?.season ?? null,
    shadeTolerance: grass?.shadeTolerance ?? null,
    sunlight: normalizeSunlight(sunlight).canonical,
  })
  return kind ? SHADE_SEED_PICKS[kind] : null
}

/**
 * The one in-result pick: this grass's seed, bag count sized to the high end
 * of the overseed range. Null for grasses with no seed or a demoted ASIN.
 */
export function getSeedingResultPick(
  result: { grassType: string; seedQuantityEstimate?: { overseedPounds: string } },
  now?: Date,
): ToolResultPickData | null {
  const seed = SEED_BY_GRASS[normalizeGrass(result.grassType)]
  if (!seed) return null
  const [link] = resolvePicks([seed])
  if (!link) return null
  return buildToolResultPick({
    toolSlug: 'seeding-calculator',
    product: {
      name: link.name,
      affiliateUrl: link.affiliateUrl,
      asin: link.asin,
      imageUrl: link.imageUrl,
      reason: link.reason,
    },
    totalLbs: upperBoundLbs(result.seedQuantityEstimate?.overseedPounds),
    now,
  })
}
